import React, { useEffect, useState, useCallback } from "react";
import Switch from "@mui/material/Switch";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NoDataImg from "../../../assets/img/no-data.gif";
import NavBar from "../../structure/NavBar";
import { NavBarTop } from "../../structure/NavBarTop";
import { useDispatch, useSelector } from "react-redux";
import { showDangerToast, showToast } from "../../constants/toaster";
import { FullImageView } from "../../custom/FullImageView";
import DropBox from "../../custom/DropBox";
import {
  updateLoading,
  updateTopHeader,
} from "../../../redux/features/common/commonSlice";
import urls from "../../constants/apiurl";
import ApiCaller from "../../constants/ApiCaller";
import InsufficientPermission from "../../custom/InSufficientPermission";
import { s3 } from "../../constants/awsConfig";

const label = { inputProps: { "aria-label": "Size switch demo" } };

// Custom styles for better table appearance
const tableStyles = {
  productName: {
    minWidth: "150px",
  },
  description: {
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  imageContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "5px",
    maxWidth: "120px",
  },
  productImage: {
    width: "40px",
    height: "40px",
    borderRadius: "5px",
    cursor: "pointer",
    objectFit: "cover",
    border: "1px solid #ddd",
    transition: "transform 0.2s",
  },
  actionButtons: {
    minWidth: "100px",
  },
};

const ServiceList = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    txt: "",
    merchantId: "",
    active: true,
    startDate: null,
    endDate: null,
    categoryId: "",
    subCategoryId: "",
  });
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [page, setPage] = useState(1);
  const [pagerecord, setPagerecord] = useState({
    totalItems: 0,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10,
  });
  const [imageUrl, setImageUrl] = useState(false);
  const [fullSizePreview, setFullSizePreview] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedServiceForUpdate, setSelectedServiceForUpdate] =
    useState(null);
  const [categories, setCategories] = useState([]);
  const [updateFormData, setUpdateFormData] = useState({
    serviceName: "",
    serviceDescription: "",
    categoryId: "",
    serviceEstPrice: "",
    serviceTime: "",
    bookServiceType: "",
    servicePaymentMode: "",
    serviceDeliveryType: "",
    expressPrice: "0",
    expressTime: "",
    serviceImages: [],
  });
  const [existingImages, setExistingImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({
    uploading: false,
    progress: 0,
    total: 0,
  });

  const LOADING_ENABLE = useSelector((state) => state?.loading);
  const userData = useSelector((state) => state?.userData);
  const dispatch = useDispatch();

  // Permission logic - similar to other components
  const userPermissions = useSelector((state) => state?.permission);
  let currentModulePermission;
  let viewPermission = true;
  let editPermission = true;
  let deletePermission = true;
  let activePermission = true;

  if (userPermissions?.modules.length > 0) {
    currentModulePermission = userPermissions.modules.find((element) => {
      if (element.moduleId === "29") {
        // Assuming moduleId for Service Manager
        return element;
      }
    })?.permissions;
    if (currentModulePermission) {
      viewPermission =
        currentModulePermission.find((element) => {
          if (element.name === "VIEW") {
            return element;
          }
        })?.isEnable || false;
      editPermission =
        currentModulePermission.find((element) => {
          if (element.name === "EDIT") {
            return element;
          }
        })?.isEnable || false;
      deletePermission =
        currentModulePermission.find((element) => {
          if (element.name === "DELETE") {
            return element;
          }
        })?.isEnable || false;
      activePermission =
        currentModulePermission.find((element) => {
          if (element.name === "ACTIVE") {
            return element;
          }
        })?.isEnable || false;
    }
  }

  useEffect(() => {
    dispatch(updateTopHeader("Service Manager"));
    // Initial load when component mounts
    if (viewPermission) {
      fetchServiceList();
      fetchServiceCategories();
    }
  }, []);

  const fetchServiceCategories = useCallback(async (parentId = "") => {
    try {
      const path = `${urls.serviceCategories}?parentId=${parentId}&service=true`;
      const res = await ApiCaller.get({}, path, userData?.access_token);
      const response = await res.response.json();
      if (response.status === "200" || response.status == 200) {
        if (parentId) {
          setSubCategoryList(response.data || []);
        } else {
          setCategoryList(response.data || []);
        }
      } else {
        showDangerToast(response.message);
      }
    } catch (error) {
      showDangerToast(error.message);
    }
  }, [userData?.access_token]);

  const fetchServiceList = useCallback(async () => {
    let startDateObject = null;
    let endDateObject = null;
    if (filters.startDate?.toDate()) {
      let newDate = new Date(filters.startDate?.toDate());
      startDateObject = newDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    }
    if (filters.endDate?.toDate()) {
      let newDate = new Date(filters.endDate?.toDate());
      endDateObject = newDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    }

    try {
      dispatch(updateLoading(true));
      const postJson = {
        sort: "createdAt",
        order: false,
        page: pagerecord?.pageNumber - 1 || 0,
        limit: pagerecord.pageSize || 10,
        active: filters.active,
        merchantId: filters.merchantId?.trim() || "",
        txt: filters.txt?.trim() || "",
        startDate: startDateObject,
        endDate: endDateObject,
        categoryId: filters.categoryId || "",
        subCategoryId: filters.subCategoryId || "",
      };
      let path = urls.serviceList;
      let res = await ApiCaller.post(postJson, path, userData?.access_token);
      const response = await res.response.json();
      if (response.status == 200 || response.status == "200") {
        dispatch(updateLoading(false));
        console.log("Service List Response:", response?.data?.items); // Debug log
        console.log(
          "First item approved status:",
          response?.data?.items?.[0]?.approved
        ); // Debug approved field
        setData(response?.data?.items || []);
        setPagerecord({
          totalItems: response?.data?.totalItems,
          totalPages: response?.data?.totalPages,
          pageNumber: response?.data?.pageNumber + 1, // Backend returns 0-based, UI uses 1-based
          pageSize: response?.data?.pageSize || 10,
        });
      } else {
        dispatch(updateLoading(false));
        showDangerToast(response.message);
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error(error);
      showDangerToast(error.message);
    }
  }, [filters, pagerecord, dispatch, userData?.access_token]);

  useEffect(() => {
    if (viewPermission) {
      fetchServiceList();
    }
  }, [pagerecord?.pageNumber]);

  const handleSearch = useCallback(() => {
    setPage(1);
    setPagerecord((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
    // Trigger search after setting page to 1
    setTimeout(() => {
      fetchServiceList();
    }, 100);
  }, [fetchServiceList]);

  const handleReset = useCallback(() => {
    setFilters({
      txt: "",
      merchantId: "",
      active: true,
      startDate: null,
      endDate: null,
      categoryId: "",
      subCategoryId: "",
    });
    setSubCategoryList([]);
    setPage(1);
    setPagerecord((prev) => ({
      ...prev,
      pageNumber: 1,
    }));
    // Trigger search after resetting filters
    setTimeout(() => {
      fetchServiceList();
    }, 100);
  }, [fetchServiceList]);

  const handleChange = (event, value) => {
    setPage(value);
    setPagerecord({
      totalItems: pagerecord?.totalItems,
      totalPages: pagerecord?.totalPages,
      pageNumber: value,
      pageSize: pagerecord?.pageSize || 10,
    });
  };

  // Handle edit service button click
  const handleEditService = (service) => {
    setSelectedServiceForUpdate(service);
    setUpdateFormData({
      serviceName: service.serviceName || "",
      serviceDescription: service.serviceDescription || "",
      categoryId: service.category || "",
      serviceEstPrice: service.serviceEstPrice?.toString() || "",
      serviceTime: service.serviceTime || "",
      bookServiceType: service.bookServiceType || "",
      servicePaymentMode: service.servicePaymentMode || "",
      serviceDeliveryType: service.serviceDeliveryType || "",
      expressPrice: service.expressPrice?.toString() || "0",
      expressTime: service.expressTime || "",
    });
    setExistingImages(service.serviceImages || []);
    setSelectedFiles([]);
    setNewImagePreviews([]);
    setShowUpdateModal(true);
    fetchServiceCategories();
  };

  // Handle update modal close
  const handleUpdateModalClose = () => {
    setShowUpdateModal(false);
    setSelectedServiceForUpdate(null);
    setUpdateFormData({
      serviceName: "",
      serviceDescription: "",
      categoryId: "",
      serviceEstPrice: "",
      serviceTime: "",
      bookServiceType: "",
      servicePaymentMode: "",
      serviceDeliveryType: "",
      expressPrice: "0",
      expressTime: "",
    });
    setSelectedFiles([]);
    setExistingImages([]);
    setNewImagePreviews([]);
    setUploadProgress({ uploading: false, progress: 0, total: 0 });
  };

  // Handle form input changes
  const handleUpdateFormChange = (field) => (e) => {
    setUpdateFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  // Handle file selection
  const handleFileSelect = (files) => {
    if (existingImages.length + selectedFiles.length + files.length > 5) {
      showDangerToast("Maximum 5 images allowed");
      return;
    }

    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);

    // Create previews for new files
    const previews = newFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith("image/") ? "image" : "video",
    }));
    setNewImagePreviews(previews);
  };

  // Remove existing image
  const removeExistingImage = (index) => {
    const updatedImages = existingImages.filter((_, i) => i !== index);
    setExistingImages(updatedImages);
  };

  // Remove new selected file
  const removeSelectedFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = newImagePreviews.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    setNewImagePreviews(updatedPreviews);
  };

  // Upload files to S3
  const uploadFilesToS3 = async (files) => {
    try {
      setUploadProgress({ uploading: true, progress: 0, total: files.length });
      const results = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileKey = `services/${Date.now()}_${file.name}`;

        const params = {
          Bucket: "your-s3-bucket-name",
          Key: fileKey,
          Body: file,
          ContentType: file.type,
        };

        try {
          await s3.upload(params).promise();
          const fileUrl = `https://your-s3-bucket-url/${fileKey}`;
          results.push(fileUrl);
          setUploadProgress((prev) => ({
            ...prev,
            progress: prev.progress + 1,
          }));
        } catch (error) {
          console.error("Error uploading file:", error);
          showDangerToast(`Failed to upload ${file.name}`);
        }
      }

      setUploadProgress({ uploading: false, progress: 0, total: 0 });
      return results;
    } catch (error) {
      console.error("Error uploading files to S3:", error);
      setUploadProgress({ uploading: false, progress: 0, total: 0 });
      throw error;
    }
  };

  // Handle service update
  const handleServiceUpdate = async () => {
    try {
      // Validate required fields
      if (!updateFormData.serviceName.trim()) {
        showDangerToast("Service name is required");
        return;
      }
      if (!updateFormData.categoryId) {
        showDangerToast("Category is required");
        return;
      }
      if (
        !updateFormData.serviceEstPrice ||
        parseFloat(updateFormData.serviceEstPrice) <= 0
      ) {
        showDangerToast("Valid estimated price is required");
        return;
      }

      dispatch(updateLoading(true));

      // Upload new files if any
      let newImageUrls = [];
      if (selectedFiles.length > 0) {
        try {
          newImageUrls = await uploadFilesToS3(selectedFiles);
        } catch (error) {
          showDangerToast("Failed to upload new images");
          dispatch(updateLoading(false));
          return;
        }
      }

      // Combine existing images with new uploaded images
      const allImages = [...existingImages, ...newImageUrls];

      // Prepare update data
      const updateData = {
        serviceId: selectedServiceForUpdate.id,
        serviceName: updateFormData.serviceName.trim(),
        serviceDescription: updateFormData.serviceDescription.trim(),
        categoryId: updateFormData.categoryId,
        serviceImages: allImages,
        serviceEstPrice: parseFloat(updateFormData.serviceEstPrice),
        serviceTime: updateFormData.serviceTime,
        bookServiceType: updateFormData.bookServiceType,
        servicePaymentMode: updateFormData.servicePaymentMode,
        serviceDeliveryType: updateFormData.serviceDeliveryType,
        expressPrice: parseFloat(updateFormData.expressPrice) || 0,
        expressTime: updateFormData.expressTime,
      };

      // Send update request
      const path = urls.updateService;
      const res = await ApiCaller.put(updateData, path, userData?.access_token);
      const response = await res.response.json();

      if (response.status === 200) {
        showToast("Service updated successfully");
        handleUpdateModalClose();
        fetchServiceList();
      } else {
        showDangerToast(response.message || "Failed to update service");
      }
    } catch (error) {
      console.error("Error updating service:", error);
      showDangerToast("Failed to update service");
    } finally {
      dispatch(updateLoading(false));
    }
  };

  const handleStatusChange = async (event, id) => {
    try {
      dispatch(updateLoading(true));
      let path = urls.changeServiceStatus + "/" + id;
      let res = await ApiCaller.put({}, path, userData?.access_token);
      const response = await res.response.json();
      if (response.status === 200) {
        dispatch(updateLoading(false));
        showToast(response.message);
        fetchServiceList();
      } else {
        dispatch(updateLoading(false));
        showDangerToast(response.message);
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error(error);
      showDangerToast(error.message);
    }
  };

  const handleApproveReject = async (id, action) => {
    const actionText = action === "approve" ? "approve" : "reject";
    const confirmMessage = `Are you sure you want to ${actionText} this service?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      dispatch(updateLoading(true));
      let path = urls.approveRejectService + "/" + id;
      const postJson = {
        action: action, // 'approve' or 'reject'
      };
      let res = await ApiCaller.put(postJson, path, userData?.access_token);
      const response = await res.response.json();
      if (response.status === 200 || response.status === "200") {
        dispatch(updateLoading(false));
        showToast(response?.message);
        fetchServiceList();
      } else {
        dispatch(updateLoading(false));
        showDangerToast(response.message);
      }
    } catch (error) {
      dispatch(updateLoading(false));
      console.error(error);
      showDangerToast(error.message);
    }
  };

  const serialNumber = (i) => {
    return i + 1 + (page - 1) * (pagerecord.pageSize || 10);
  };

  // Handle text input changes
  const handleTextChange = useCallback(
    (field) => (e) => {
      setFilters((f) => ({ ...f, [field]: e.target.value }));
    },
    []
  );

  // Handle checkbox change
  const handleActiveChange = useCallback((e) => {
    setFilters((f) => ({ ...f, active: e.target.checked }));
  }, []);

  // Handle date changes
  const handleStartDateChange = useCallback((newValue) => {
    setFilters((f) => ({ ...f, startDate: newValue }));
  }, []);

  const handleEndDateChange = useCallback((newValue) => {
    setFilters((f) => ({ ...f, endDate: newValue }));
  }, []);

  // Handle Enter key press for search
  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <>
      <NavBar />
      <div className="main-content">
        <NavBarTop />
        <div className="header">
          <div className="logo">
            <div className="header bg-gradient-primary pb-8 pt-5 pt-md-8"></div>
          </div>
        </div>
        {viewPermission ? (
          <div className="container-fluid mt--7">
            <div className="row">
              <div className="col text-left">
                <div className="card shadow">
                  <div className="card-header border-0">
                    <div className="row align-items-center text-left">
                      <div className="col">
                        <h3 className="mb-0">Service Manager</h3>
                      </div>
                    </div>
                  </div>
                  <div className="card-header border-0">
                    <div className="row align-items-center">
                      <div className="col-md-3 mb-2">
                        <input
                          className="form-control"
                          placeholder="Search Service"
                          value={filters.txt}
                          onChange={handleTextChange("txt")}
                          onKeyPress={handleKeyPress}
                        />
                      </div>
                      <div className="col-md-3 mb-2">
                        <input
                          className="form-control"
                          placeholder="Merchant ID"
                          value={filters.merchantId}
                          onChange={handleTextChange("merchantId")}
                          onKeyPress={handleKeyPress}
                        />
                      </div>
                      <div className="col-md-3 mb-2">
                        <select
                          className="form-control"
                          value={filters.categoryId}
                          onChange={(e) => {
                            const categoryId = e.target.value;
                            setFilters(prev => ({
                              ...prev,
                              categoryId,
                              subCategoryId: "" // Reset subcategory when category changes
                            }));
                            if (categoryId) {
                              fetchServiceCategories(categoryId); // Fetch subcategories
                            } else {
                              setSubCategoryList([]); // Clear subcategories if no category selected
                            }
                          }}
                        >
                          <option value="">Select Category</option>
                          {categoryList.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3 mb-2">
                        <select
                          className="form-control"
                          value={filters.subCategoryId}
                          onChange={(e) => {
                            setFilters(prev => ({
                              ...prev,
                              subCategoryId: e.target.value
                            }));
                          }}
                          disabled={!filters.categoryId} // Disable if no category selected
                        >
                          <option value="">Select Subcategory</option>
                          {subCategoryList.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={filters.active}
                            id="activeCheck"
                            onChange={handleActiveChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="activeCheck"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4 mb-2">
                        <button
                          className="btn btn-primary btn-sm mr-2"
                          onClick={handleSearch}
                          disabled={LOADING_ENABLE}
                        >
                          {LOADING_ENABLE ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm mr-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Searching...
                            </>
                          ) : (
                            "Search"
                          )}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={handleReset}
                          disabled={LOADING_ENABLE}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label className="form-control-label text-sm font-weight-bold text-muted mb-2 d-block">
                                <i className="fas fa-calendar-alt mr-1"></i>From
                                Date
                              </label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  disableFuture
                                  value={filters.startDate}
                                  onChange={handleStartDateChange}
                                  maxDate={filters.endDate}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      fullWidth: true,
                                      variant: "outlined",
                                      placeholder: "Select from date",
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      height: "38px",
                                      fontSize: "14px",
                                      backgroundColor: "#fff",
                                      border: "1px solid #dee2e6",
                                      borderRadius: "0.375rem",
                                      "&:hover": {
                                        borderColor: "#86b7fe",
                                      },
                                      "&.Mui-focused": {
                                        borderColor: "#86b7fe",
                                        boxShadow:
                                          "0 0 0 0.2rem rgba(13, 110, 253, 0.25)",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      display: "none",
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-0">
                              <label className="form-control-label text-sm font-weight-bold text-muted mb-2 d-block">
                                <i className="fas fa-calendar-alt mr-1"></i>To
                                Date
                              </label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  disableFuture
                                  value={filters.endDate}
                                  onChange={handleEndDateChange}
                                  minDate={filters.startDate}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      fullWidth: true,
                                      variant: "outlined",
                                      placeholder: "Select to date",
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      height: "38px",
                                      fontSize: "14px",
                                      backgroundColor: "#fff",
                                      border: "1px solid #dee2e6",
                                      borderRadius: "0.375rem",
                                      "&:hover": {
                                        borderColor: "#86b7fe",
                                      },
                                      "&.Mui-focused": {
                                        borderColor: "#86b7fe",
                                        boxShadow:
                                          "0 0 0 0.2rem rgba(13, 110, 253, 0.25)",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      display: "none",
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    {data.length ? (
                      <table className="table align-items-center table-flush table-hover">
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">S/No.</th>
                            <th scope="col">Service Name</th>
                            {/* <th scope='col'>Merchant Name</th> */}
                            <th scope="col">Description</th>
                            {/* <th scope='col'>Category</th> */}
                            <th scope="col">Price</th>
                            <th scope="col">Service Time</th>
                            <th scope="col">Delivery Type</th>
                            {/* <th scope='col'>Currency</th> */}
                            <th scope="col">Media</th>
                            {/* <th scope='col'>Status</th> */}
                            <th scope="col">Approval</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item, i) => (
                            <tr key={item.uuid}>
                              <td>{serialNumber(i)}</td>
                              <td style={tableStyles.productName}>
                                <div>
                                  <strong>{item.serviceName || "-"}</strong>
                                  {item.merchantId && (
                                    <div className="small text-muted">
                                      Merchant ID: {item.merchantId}
                                    </div>
                                  )}
                                </div>
                              </td>
                              {/* <td>{item?.merchantBusinessResponse?.businessName || '-'}</td> */}
                              <td>
                                <div style={tableStyles.description}>
                                  {item.serviceDescription ? (
                                    <span title={item.serviceDescription}>
                                      {item.serviceDescription.length > 50
                                        ? `${item.serviceDescription.substring(
                                          0,
                                          50
                                        )}...`
                                        : item.serviceDescription}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              </td>
                              {/* <td>
                                <div>
                                  {item.category ? (
                                    <span className="badge badge-primary">
                                      {typeof item.category === 'object' ? item.category?.name || '-' : item.category || '-'}
                                    </span>
                                  ) : (
                                    <span className="text-muted">No Category</span>
                                  )}
                                </div>
                              </td> */}
                              <td>
                                <strong>
                                  {item.currencyCode || "GHS "}
                                  {item.serviceEstPrice || "0"}
                                </strong>
                                {item.expressPrice && (
                                  <div className="small text-success">
                                    Express: {item.currencyCode || "GHS "}
                                    {item.expressPrice}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div>
                                  <span className="badge badge-info">
                                    {item.serviceTime || "0"} mins
                                  </span>
                                  {item.expressTime && (
                                    <div className="small text-muted">
                                      Express: {item.expressTime} mins
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span
                                  className={`badge ${item.serviceDeliveryType === "STANDARD"
                                      ? "badge-primary"
                                      : "badge-warning"
                                    }`}
                                >
                                  {item.serviceDeliveryType || "N/A"}
                                </span>
                              </td>
                              {/* <td>
                                <span className="badge badge-secondary">
                                  {item.currencyCode || 'N/A'}
                                </span>
                              </td> */}
                              <td>
                                <div style={tableStyles.imageContainer}>
                                  {item.serviceImages &&
                                    item.serviceImages.length > 0 ? (
                                    item.serviceImages.map((media, idx) => {
                                      const videoExtensions = [
                                        ".mp4",
                                        ".webm",
                                        ".ogg",
                                        ".avi",
                                        ".mov",
                                        ".wmv",
                                        ".flv",
                                        ".mkv",
                                      ];
                                      const isVideo =
                                        videoExtensions.some((ext) =>
                                          media
                                            .toLowerCase()
                                            .includes(ext.toLowerCase())
                                        ) ||
                                        media.toLowerCase().includes("video");

                                      return isVideo ? (
                                        <div
                                          key={idx}
                                          style={{
                                            ...tableStyles.productImage,
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#f8f9fa",
                                          }}
                                          onClick={() => {
                                            setFullSizePreview(true);
                                            setImageUrl(media);
                                          }}
                                          onMouseEnter={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1.1)")
                                          }
                                          onMouseLeave={(e) =>
                                          (e.currentTarget.style.transform =
                                            "scale(1)")
                                          }
                                          title="Click to view video"
                                        >
                                          <i
                                            className="fas fa-play-circle"
                                            style={{
                                              fontSize: "20px",
                                              color: "#007bff",
                                            }}
                                          ></i>
                                          <div
                                            style={{
                                              position: "absolute",
                                              bottom: "2px",
                                              right: "2px",
                                              backgroundColor:
                                                "rgba(0,0,0,0.7)",
                                              color: "white",
                                              fontSize: "8px",
                                              padding: "1px 3px",
                                              borderRadius: "2px",
                                            }}
                                          >
                                            VIDEO
                                          </div>
                                        </div>
                                      ) : (
                                        <img
                                          key={idx}
                                          src={media}
                                          style={tableStyles.productImage}
                                          onClick={() => {
                                            setFullSizePreview(true);
                                            setImageUrl(media);
                                          }}
                                          onMouseEnter={(e) =>
                                          (e.target.style.transform =
                                            "scale(1.1)")
                                          }
                                          onMouseLeave={(e) =>
                                          (e.target.style.transform =
                                            "scale(1)")
                                          }
                                          alt="service"
                                          title="Click to view full size"
                                        />
                                      );
                                    })
                                  ) : (
                                    <span className="text-muted small">
                                      No media
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* <td>
                                <Switch
                                  {...label}
                                  checked={item.active}
                                  disabled={!activePermission}
                                  onChange={e => handleStatusChange(e, item.uuid)}
                                  size='small'
                                />
                                {item.bookServiceType && (
                                  <div className="small text-muted mt-1" title="Booking Type">
                                    <i className="fas fa-calendar"></i> {item.bookServiceType}
                                  </div>
                                )}
                              </td> */}
                              <td>
                                <span
                                  className={`badge ${item.approved
                                      ? "badge-success"
                                      : "badge-warning"
                                    }`}
                                >
                                  {item.approved ? "Approved" : "Pending"}
                                </span>
                              </td>
                              <td style={tableStyles.actionButtons}>
                                <div className="d-flex gap-1">
                                  {item.approved ? (
                                    // If service is approved, show reject button
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() =>
                                        handleApproveReject(item.uuid, "reject")
                                      }
                                      disabled={
                                        LOADING_ENABLE || !editPermission
                                      }
                                      title="Reject Service"
                                    >
                                      ❌
                                    </button>
                                  ) : (
                                    // If service is not approved, show approve button
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={() =>
                                        handleApproveReject(
                                          item.uuid,
                                          "approve"
                                        )
                                      }
                                      disabled={
                                        LOADING_ENABLE || !editPermission
                                      }
                                      title="Approve Service"
                                    >
                                      ✔️
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-primary btn-sm mr-2"
                                    onClick={() => handleEditService(item)}
                                    disabled={!editPermission || LOADING_ENABLE}
                                  >
                                    <i className="fas fa-edit"></i> Edit
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center">
                        <img width={"25%"} src={NoDataImg} alt="No Data" />
                        <br />
                        <label style={{ fontWeight: "bold" }}>
                          {LOADING_ENABLE ? (
                            <>Loading Data.....</>
                          ) : (
                            <>No Data Found</>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                  {data.length > 0 && (
                    <div className="card-footer py-4">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <div>
                          <span>Total record: {pagerecord.totalItems}</span>
                          <span className="ml-3 text-muted">
                            Page {pagerecord.pageNumber} of{" "}
                            {pagerecord.totalPages}
                          </span>
                        </div>
                        <Pagination
                          className="custom-pagination-class"
                          count={pagerecord.totalPages}
                          page={page}
                          onChange={handleChange}
                          color="secondary"
                          showFirstButton
                          showLastButton
                        />
                      </Box>
                    </div>
                  )}
                  <FullImageView
                    src={imageUrl}
                    show={fullSizePreview}
                    onHide={() => setFullSizePreview(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <InsufficientPermission />
        )}

        {/* Service Update Modal */}
        {showUpdateModal && (
          <div
            className="modal fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1050,
              overflow: "auto",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleUpdateModalClose();
              }
            }}
          >
            <div
              className="modal-dialog modal-lg"
              style={{
                margin: "20px auto",
                maxWidth: "800px",
                position: "relative",
              }}
            >
              <div
                className="modal-content"
                style={{
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">Update Service</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={handleUpdateModalClose}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div
                  className="modal-body"
                  style={{
                    maxHeight: "calc(90vh - 120px)",
                    overflowY: "auto",
                  }}
                >
                  <form>
                    <div className="row">
                      {/* Service Name */}
                      <div className="col-md-6 mb-3">
                        <label htmlFor="serviceName" className="form-label">
                          Service Name *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="serviceName"
                          value={updateFormData.serviceName}
                          onChange={handleUpdateFormChange("serviceName")}
                          required
                        />
                      </div>

                      {/* Category */}
                      <div className="col-md-6 mb-3">
                        <label htmlFor="category" className="form-label">
                          Category *
                        </label>
                        <select
                          className="form-control"
                          id="category"
                          value={updateFormData.categoryId}
                          onChange={handleUpdateFormChange("categoryId")}
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map((category) => (
                            <option key={category.key} value={category.key}>
                              {category.value}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Estimated Price */}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="serviceEstPrice" className="form-label">
                          Estimated Price *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="serviceEstPrice"
                          value={updateFormData.serviceEstPrice}
                          onChange={handleUpdateFormChange("serviceEstPrice")}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      {/* Service Time */}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="serviceTime" className="form-label">
                          Service Time (minutes)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="serviceTime"
                          value={updateFormData.serviceTime}
                          onChange={handleUpdateFormChange("serviceTime")}
                          min="0"
                        />
                      </div>

                      {/* Book Service Type */}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="bookServiceType" className="form-label">
                          Service Type
                        </label>
                        <select
                          className="form-control"
                          id="bookServiceType"
                          value={updateFormData.bookServiceType}
                          onChange={handleUpdateFormChange("bookServiceType")}
                        >
                          <option value="">Select Type</option>
                          <option value="ALL">All</option>
                          <option value="ON_SERVICE_LOCATION">On Store</option>
                          <option value="HOME_SERVICE">Home Service</option>
                        </select>
                      </div>

                      {/* Payment Mode */}
                      <div className="col-md-4 mb-3">
                        <label
                          htmlFor="servicePaymentMode"
                          className="form-label"
                        >
                          Payment Mode
                        </label>
                        <select
                          className="form-control"
                          id="servicePaymentMode"
                          value={updateFormData.servicePaymentMode}
                          onChange={handleUpdateFormChange(
                            "servicePaymentMode"
                          )}
                        >
                          <option value="">Select Mode</option>
                          <option value="PRE_PAID">Pre Paid</option>
                          <option value="POST_PAID">Post Paid</option>
                        </select>
                      </div>

                      {/* Delivery Type */}
                      <div className="col-md-4 mb-3">
                        <label
                          htmlFor="serviceDeliveryType"
                          className="form-label"
                        >
                          Delivery Type
                        </label>
                        <select
                          className="form-control"
                          id="serviceDeliveryType"
                          value={updateFormData.serviceDeliveryType}
                          onChange={handleUpdateFormChange(
                            "serviceDeliveryType"
                          )}
                        >
                          <option value="">Select Type</option>
                          <option value="ALL">All</option>
                          <option value="STANDARD">Standard</option>
                          <option value="EXPRESS">Express</option>
                        </select>
                      </div>

                      {/* Express Price */}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="expressPrice" className="form-label">
                          Express Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="expressPrice"
                          value={updateFormData.expressPrice}
                          onChange={handleUpdateFormChange("expressPrice")}
                          min="0"
                          step="0.01"
                        />
                      </div>

                      {/* Express Time */}
                      <div className="col-md-4 mb-3">
                        <label htmlFor="expressTime" className="form-label">
                          Express Time (minutes)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="expressTime"
                          value={updateFormData.expressTime}
                          onChange={handleUpdateFormChange("expressTime")}
                          min="0"
                        />
                      </div>

                      {/* Description */}
                      <div className="col-12 mb-3">
                        <label
                          htmlFor="serviceDescription"
                          className="form-label"
                        >
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          id="serviceDescription"
                          rows="3"
                          value={updateFormData.serviceDescription}
                          onChange={handleUpdateFormChange(
                            "serviceDescription"
                          )}
                        />
                      </div>

                      {/* Images Section */}
                      <div className="col-12 mb-3">
                        <label className="form-label">Images (Max 5)</label>

                        {/* Existing Images */}
                        {existingImages.length > 0 && (
                          <div className="mb-3">
                            <h6>Current Images:</h6>
                            <div className="d-flex flex-wrap gap-2">
                              {existingImages.map((image, index) => (
                                <div key={index} className="position-relative">
                                  <img
                                    src={image}
                                    alt="Existing"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      borderRadius: "5px",
                                      border: "1px solid #ddd",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute"
                                    style={{
                                      top: "-5px",
                                      right: "-5px",
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      padding: "0",
                                      fontSize: "12px",
                                    }}
                                    onClick={() => removeExistingImage(index)}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* New Image Selection */}
                        {existingImages.length + selectedFiles.length < 5 && (
                          <div className="mb-3">
                            <h6>Add New Images:</h6>
                            <DropBox
                              onDrop={handleFileSelect}
                              maxFiles={5 - existingImages.length}
                            />
                          </div>
                        )}

                        {/* New Image Previews */}
                        {newImagePreviews.length > 0 && (
                          <div className="mb-3">
                            <h6>New Images Preview:</h6>
                            <div className="d-flex flex-wrap gap-2">
                              {newImagePreviews.map((preview, index) => (
                                <div key={index} className="position-relative">
                                  <img
                                    src={preview.preview}
                                    alt="Preview"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                      borderRadius: "5px",
                                      border: "1px solid #ddd",
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute"
                                    style={{
                                      top: "-5px",
                                      right: "-5px",
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      padding: "0",
                                      fontSize: "12px",
                                    }}
                                    onClick={() => removeSelectedFile(index)}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <small className="text-muted">
                          Total images:{" "}
                          {existingImages.length + selectedFiles.length}/5
                        </small>
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  {/* Upload Progress Display */}
                  {uploadProgress.uploading && (
                    <div className="flex-grow-1 mr-3">
                      <small className="text-muted d-block">
                        Uploading images: {uploadProgress.progress}/
                        {uploadProgress.total}
                      </small>
                      <div className="progress" style={{ height: "6px" }}>
                        <div
                          className="progress-bar bg-info"
                          style={{
                            width: `${(uploadProgress.progress / uploadProgress.total) *
                              100
                              }%`,
                            transition: "width 0.3s ease",
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleUpdateModalClose}
                    disabled={LOADING_ENABLE || uploadProgress.uploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleServiceUpdate}
                    disabled={LOADING_ENABLE || uploadProgress.uploading}
                  >
                    {LOADING_ENABLE ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Updating...
                      </>
                    ) : (
                      "Update Service"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ServiceList;
